import { macroCondition, isTesting } from '@embroider/macros';
import { registerDestructor } from '@ember/destroyable';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';
import _ from 'lodash';
import Feed, { LIVE_STREAM_PAUSE_GRACE_MS } from './feed';
export default class BroadcastFeed extends Feed {
  kind = 'broadcast'

  @tracked nowPlayingRecord;

  constructor(nowPlayingObject, options = {}) {
    super(nowPlayingObject, options);
    this.nowPlayingRecord = nowPlayingObject;
    this.startTask.perform();
    this.listenForVisibilityChanges();
    registerDestructor(this, this.teardown.bind(this));
  }

  // Background tabs throttle the poll loop; refetch on return to the foreground.
  listenForVisibilityChanges() {
    if (macroCondition(isTesting())) {
      return;
    }
    this.handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && this.isLoaded) {
        this.loadNowPlayingDataTask.perform();
      }
    };
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  refresh() {
    return this.updateAttributesTask.perform(this.nowPlayingRecord);
  }

  get isLive() {
    return true; // broadcast feed = live feed
  }

  // A live broadcast is pinned to the live edge, never scrubbed in place. The
  // sound can't be trusted to say so: a relay serving an endless broadcast
  // reports a seekable window like any recording, and NativeAudio measures
  // seekability off that window whatever the duration says. Keying the scrubber
  // off the feed keeps the playhead on the live edge and makes dragging rewind
  // into the recorded PlaybackFeed instead.
  get isLiveEdge() {
    return true;
  }

  get imageUrl() {
    return this.imageSource?.medium_square?.url
  }

  get url() {
    return this.nowPlayingRecord?.streamUrl;
  }

  get canPlay() {
    return !!this.url
  }

  // The live stream is a public Icecast/Shoutcast URL — a remote device can
  // fetch it directly, so the remote URL is just the stream URL (no fetch).
  get canRemotePlay() {
    return !!this.url
  }

  get remotePlaybackUrl() {
    return this.url
  }

  get isRewindable() {
    return (
      this.allowsLiveRewind &&
      this.stream?.allowsPlayback &&
      !!this.airing?.playbackUrl
    );
  }

  // Pinned to the live edge — there's nothing ahead of live to skip to.
  get isFastForwardable() {
    return false;
  }

  startTask = task(async () => {
    if (this.settings.debug) {
      console.log(`built broadcast feed for nowPlayingRecord ${this.nowPlayingRecord.id}, starting monitors`);
    }

    try {
      await this.updateAttributesTask.perform(this.nowPlayingRecord) // initial load
      await this.loadCableTask.perform();
      // The launch snapshot may be a service worker cache hit, so don't wait for the poll loop.
      this.loadNowPlayingDataTask.perform();
    } catch (e) {
      console.error(e)
      //no op
    }
    this.loopTask.perform();

    // A live broadcast that can rewind will need the HLS connection the moment
    // the listener drags the scrubber. Pull its lazy hls.js chunk down now so
    // the rewind itself is only audio, not a script download.
    if (this.isRewindable) {
      this.stereo.prewarmConnection?.('HLS');
    }

    next(() => this.isLoaded = true)
  });

  loopTask = task(async () => {
    if (macroCondition(isTesting())) {
      return;
    }
    while (true) {
      try {
        if (this.isLoaded && !this.cableConnection?.connected) {
          await timeout(10000);
        } else if (this.isLoaded && this.cableConnection?.connected) {
          await timeout(60000);
        } else {
          await timeout(1000);
          continue;
        }

        // If a token refresh is in flight (e.g. browser resumed from suspension),
        // wait for it to complete before polling with a potentially stale token.
        if (this.settings.loadLaunchRecordTask.isRunning) {
          await this.settings.loadLaunchRecordTask.last;
        }

        await this.loadNowPlayingDataTask.perform();
      } catch (e) {
        //no op
        await timeout(10000);
      }
    }
  });

  get cableConnected() {
    return this.cableConnection?.connected
  }

  loadNowPlayingDataTask = task({ drop: true }, async () => {
    let nowPlaying = await this.store.queryRecord('now-playing', {
        id: this.broadcastId,
        stationId: this.station.uid,
        include: 'next_airing,next_airing.show',
        reload: true
      })
      .catch((e) => {
        console.error(e);
      });


    if (nowPlaying) {
      await this.updateAttributesTask.perform(nowPlaying);
      if (nowPlaying.updatedAt != this.nowPlayingUpdatedAt) {
        this.nowPlayingUpdatedAt = nowPlaying.updatedAt
        if (this.itemsFetched) {
          this.loadAiringItemsTask.perform()
        }
      }
    }

    await timeout(1000);
  });

  @tracked listeningSessionId = null;

  // Pause and resume share one session. The feeds service clears it on leaving for a recording.
  recordListeningSessionTask = task({ drop: true }, async () => {
    if (this.listeningSessionId) {
      return;
    }
    if (macroCondition(isTesting())) {
      return;
    }

    let adapter = this.store.adapterFor('broadcast');
    let broadcastUrl = adapter.buildURL('broadcast', this.broadcastId, { adapterOptions: { stationUid: this.station?.uid } }, 'findRecord');

    try {
      let response = await fetch(`${broadcastUrl}/listening_sessions`, {
        method: 'POST',
        cache: 'no-store',
        headers: {
          Accept: 'application/vnd.api+json',
          ...(adapter.headers ?? {}),
        },
      });
      if (!response.ok) return;

      let json = await response.json();
      this.listeningSessionId = json?.data?.id ?? null;
    } catch (e) {
      // Listening is not contingent on being counted.
    }
  });

  // A live broadcast has no end. Worth declaring because measurement can't get
  // here on its own: KLKT's relay reports a duration that grows in real time
  // from a window starting at zero, which is indistinguishable from a recording
  // still being written.
  loadDuration() {
    return Infinity;
  }

  loadStreamPauseGraceMs() {
    return LIVE_STREAM_PAUSE_GRACE_MS;
  }

  // A live stream has no timeline of its own, but the airing it's carrying does,
  // and it's the one the player's scrubber already draws.
  get timeline() {
    let airing = this.airing;
    if (!airing?.startsAt || !airing?.endsAt) return null;

    let duration = airing.endsAt.getTime() - airing.startsAt.getTime();
    if (duration <= 0) return null;

    return { duration, position: this.elapsedMs };
  }

  get mediaSessionActions() {
    if (!this.isRewindable) {
      // The timeline still goes out, so the card shows elapsed and remaining;
      // declining the seeks is what keeps it from offering a rewind we won't do.
      return { seekto: null, seekbackward: null, seekforward: null };
    }

    return {
      seekto: (positionMs) => this.rewindTo(positionMs),
      seekbackward: (offsetMs) => this.rewindTo(this.elapsedMs - offsetMs),
      // Nothing ahead of live to skip to; a no-op would leave a dead button.
      seekforward: null,
    };
  }

  // Seeking back on a live broadcast doesn't move the stream; it leaves it for
  // the recording, the same move the player's scrubber makes. Anything at or
  // past the live edge is a request to stay where we are.
  rewindTo(positionMs) {
    if (!this.airing || positionMs >= this.elapsedMs) return;

    this.feeds.scrubToFeed(this.airing, Math.max(positionMs, 0));
  }

  playTask = task({ restartable: true, maxConcurrency: 1 }, async () => {
    // Load if there's no backing connection yet — OR while casting, so a feed we
    // return to re-resolves onto the device (its prior cast connection is stale
    // once the session moved to another feed). loadTask no-ops if it's already
    // the active cast.
    let args = await this.castAwareLoadArgs(undefined, {
      useConnections: ['NativeAudio', 'Howler'],
      metadata: { airing: this.airing },
      xhr: { headers: this.session?.authenticatedHeaders },
    });
    if (args) {
      try {
        await this.stereo.playTask.linked().perform(...args);
      } catch (e) {
        throw new Error('failed to load sound', { cause: e });
      }
    }

    if (this.loadedSound?.isResolved && !this.loadedSound.isPlaying) {
      await this.loadedSound.togglePause();
    }

    await timeout(200);
  });

  loadCableTask = task(async () => {
    this.cableConnection = this.socket.subscribe(
      {
        channel: 'NowPlayingChannel',
        station_id: this.station.id,
        broadcast_id: this.broadcastId,
      },
      {
        received: (data) => {
          if (this.isDestroyed) return;
          if (this.settings.debug) {
            console.log(
              '⚡️ received NowPlayingChannel update for stream monitor, handling...'
            );
          }
          this.lastCacheKey = data?.cache;
          this.loadNowPlayingDataTask.perform();
        },
        disconnected: (data) => {
          if (this.isDestroyed) return;
          this.loadNowPlayingDataTask.perform();
        }
      }
    );
  });

  updateAttributesTask = task(async (nowPlaying) => {
    this.station = this.currentNowPlayingModel(nowPlaying, 'station')
    this.broadcast = this.currentNowPlayingModel(nowPlaying, 'broadcast')
    this.airing = this.currentNowPlayingModel(nowPlaying, 'airing')
    this.show = this.currentNowPlayingModel(nowPlaying, 'show')
    this.stream = this.currentNowPlayingModel(nowPlaying, 'stream')
    this.track = this.currentNowPlayingModel(nowPlaying, 'track')
    this.traffic = this.currentNowPlayingModel(nowPlaying, 'traffic')
    this.note = this.currentNowPlayingModel(nowPlaying, 'note')
    this.nextAiring = this.currentNowPlayingModel(nowPlaying, 'nextAiring', 'airing')

    let item = _.maxBy(_.compact([this.track, this.traffic, this.note]), (item) => new Date(item.playedAt).getTime())
    this.item = item;
    this.itemType = item?.type;

    try {
      this.feeds.updateAudioMetadata.perform(this.url, {
        station: this.station,
        broadcast:this.broadcast,
        airing: this.airing,
        nextAiring: this.nextAiring,
        show: this.show,
        stream: this.stream,
        track: this.track,
        note: this.note,
        traffic: this.traffic,
        imageSource: this.imageSource,
        timeline: this.timeline,
      })
    } catch(e) {
      console.error(`error updating audio metadata`, e)
    }
  });

  currentNowPlayingModel(nowPlayingRecord, relationshipName, modelType) {
    let id = nowPlayingRecord.belongsTo(relationshipName)?.id()

    if (id) {
      return this.store.peekRecord(modelType || relationshipName, id)
    }
  }

  teardown() {
    this.cableConnection?.unsubscribe()

    if (this.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    if (this.settings?.debug) {
      console.log('destroyed broadcast feed', this.url);
    }
  }
}
